import { useCallback, useMemo } from "react";

import {
    LoopInfoParams,
    LoopMetadataTag,
    PrincipalCollateralPair,
    StrategyDuration,
    TokenListTag,
    TokenListMetadata,
    UncertaintyType,
    LoopRouteType,
    LoopVaultInfo,
    WhirlpoolPositionExpanded,
    OracleQuote,
    VaultMintLpParams
} from "@bridgesplit/abf-sdk";
import {
    ALL_MINTS,
    AllValueType,
    decimalsToBps,
    filterNullableRecord,
    formatNum,
    getUnixTs,
    greaterThan,
    isAll,
    IS_LOCAL_NX_DEV,
    lamportsToUiAmount,
    NullableRecord,
    percentUiToDecimals,
    roundDownToDecimals,
    SortDirection,
    TIME,
    uiAmountToLamports,
    WRAPPED_SOL_MINT,
    bsMath,
    filterUndefined,
    filterTruthy,
    roundTimestamp,
    BalanceChanges,
    TIME_IN_DAYS,
    USDC_MINT
} from "@bridgesplit/utils";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useMemoizedGroupedMap, useMemoizedKeyMap, useMemoizedMap } from "@bridgesplit/ui";
import { SwapParams, SwapInstructionsResponse, WindType } from "@loopscale/pandora-types";

import {
    useLoopBestQuoteQuery,
    useLoopInfosQuery,
    useStatsQuery,
    useFlashPerpsQuery,
    useSwapInstructionsQuery,
    useWhirlpoolsQuery,
    useWhirlpoolsOffchainQuery,
    useLoopCollateralYieldLatestByMintsQuery,
    useMarketsWindQuoteIxsQuery
} from "../reducers";
import {
    BestLoopQuoteQuery,
    ExternalUnwindQuoteParams,
    ExternalWindQuoteParams,
    LoopExpanded,
    LoopPositionExpanded,
    LoopQuoteExpanded,
    LoopExpandedBase,
    AbfOrderFundingType,
    LoopExpandedMeteora,
    ParsedLoanEventExpanded,
    DerivedLoanEventActionType,
    LoanInfoSorting,
    LoanPaginationInfo,
    LoanStatusType,
    LoopExpandedOrca,
    LoopTokenPriceChange,
    OracleOHLC,
    LstBreakevenParams,
    PtBreakevenParams,
    AbfLoanExpanded,
    LoanFilterApi,
    LoansAggregate
} from "../types";
import { useOraclePrices } from "./pricing";
import {
    useAbfTypesToUiConverter,
    isEventActionMetadataType,
    isLeverageLoop,
    isWhirlpoolRouteParams,
    isLoopLoan
} from "../utils";
import {
    getZcCollateral,
    getZcLedger,
    loanCollateralGroupTableKey,
    uniqueCollateralIdentifiersFromLoanGroupKeys,
    useLoanInfos
} from "./loans";
import { usePausedExternalYieldSources } from "./external-yield";
import { getBorrowCapDetails, ALL_MARKETS_STATS_QUERY_PARAMS } from "./markets";
import { DEFAULT_STRATEGY_DURATION, MAXIMUM_PAGE_SIZE } from "../constants";
import { getClaimableWhirlpoolFees } from "./whirlpool";
import { useLiveClock, REWARDS_TICK } from "./clock";
import { useLoopscaleWalletContext } from "../wallet";
import { useTokenListContext } from "./token-list";
export function useLoopsExpanded(params: LoopInfoParams, options?: { skip?: boolean }) {
    const query = useLoopInfosQuery(params, { skip: options?.skip });

    const { getMetadata, isLoading: metadataLoading } = useTokenListContext();
    const { convertLoopVault, convertPrincipalMarketStats } = useAbfTypesToUiConverter();

    // Reuse the markets stats superset so this dedupes with useMarkets into one /stats request
    // (loops only reads principal + borrowCaps, both included in the shared params).
    const { data: statsQuery, isLoading: principalStatsLoading } = useStatsQuery(ALL_MARKETS_STATS_QUERY_PARAMS, {
        skip: options?.skip
    });

    const principalStatsMap = useMemo(() => {
        return statsQuery?.principal ? new Map(Object.entries(statsQuery.principal)) : undefined;
    }, [statsQuery]);

    const borrowCapsMap = useMemo(() => {
        return statsQuery?.borrowCaps ? new Map(Object.entries(statsQuery.borrowCaps)) : undefined;
    }, [statsQuery]);

    const whirlpoolMints = useMemo(() => {
        if (!query.data) return [];
        return Object.values(query.data)
            .map((loop) => getWhirlpoolFromLoopVault(loop))
            .filter(filterUndefined);
    }, [query.data]);

    const { data: whirlpools, isLoading: whirlpoolsLoading } = useWhirlpoolsQuery(whirlpoolMints, {
        skip: !whirlpoolMints.length || options?.skip
    });

    const { data: whirlpoolsOffchain, isLoading: whirlpoolsOffchainLoading } = useWhirlpoolsOffchainQuery(
        { addresses: whirlpoolMints },
        {
            skip: !whirlpoolMints.length || options?.skip
        }
    );

    const isLoading =
        query.isLoading || metadataLoading || principalStatsLoading || whirlpoolsLoading || whirlpoolsOffchainLoading;

    const data = useMemo(() => {
        if (!query.data || isLoading) return undefined;

        return Object.entries(query.data)
            .map(([vaultIdentifier, loopVaultRaw]): NullableRecord<LoopExpanded> => {
                const loopVault = convertLoopVault(loopVaultRaw);
                const isComingSoon = loopVault.metadataTags?.includes(LoopMetadataTag.ComingSoon) ?? false;
                const borrowCap = borrowCapsMap?.get(loopVault.principalMint);
                let principalStats = principalStatsMap?.get(loopVault.principalMint);
                if (principalStats) {
                    principalStats = convertPrincipalMarketStats(loopVault.principalMint, principalStats);
                }

                const inputToken =
                    getLoopWindType(loopVault) === WindType.Collateral
                        ? getMetadata(loopVault.collateralMint)
                        : getMetadata(loopVault.principalMint);

                // Market stats are wind-side inputs (available liquidity, borrow caps) — a loop is
                // still fully closable/manageable without them. Always null rather than undefined so
                // a missing /stats entry can't take the whole loop out via filterNullableRecord,
                // which would leave borrowers with a dead Close button. Consumers must handle null.
                const common: NullableRecord<LoopExpandedBase> = {
                    vaultIdentifier,
                    loopVault,
                    collateralToken: getMetadata(loopVault.collateralMint),
                    principalToken: getMetadata(loopVault.principalMint),
                    principalStats: principalStats ?? null,
                    borrowCap: borrowCap ?? null,
                    maxCollateralAmount: loopVault.maxCollateralAmount,
                    loopWindType: getLoopWindType(loopVault),
                    inputToken
                };

                if (!loopVault.feVisible && !params.showHidden && !IS_LOCAL_NX_DEV && !isComingSoon) {
                    return {
                        ...common,
                        // this will disable the loop in the UI
                        tokenA: undefined
                    };
                }

                const whirlpool = getWhirlpoolFromLoopVault(loopVault);
                if (whirlpool) {
                    const whirlpoolData = whirlpools?.[whirlpool];
                    // will be disable in ui
                    if (!whirlpoolData) return { ...common, whirlpool: undefined };

                    return {
                        ...common,
                        whirlpool: {
                            ...whirlpoolData,
                            tokenA: getMetadata(whirlpoolData.tokenMintA),
                            tokenB: getMetadata(whirlpoolData.tokenMintB)
                        },
                        whirlpoolOffchain: whirlpoolsOffchain?.data?.find((w) => w.address === whirlpool)
                    };
                }

                return { ...common };
            })
            .filter(filterNullableRecord);
    }, [
        query.data,
        isLoading,
        convertLoopVault,
        principalStatsMap,
        getMetadata,
        params.showHidden,
        convertPrincipalMarketStats,
        whirlpools,
        whirlpoolsOffchain,
        borrowCapsMap
    ]);

    const filteredData = useMemo(() => {
        if (!data) return undefined;
        if (params.showHidden) return data;
        return data.filter(
            (loop) =>
                loop.loopVault.feVisible || (loop.loopVault.metadataTags?.includes(LoopMetadataTag.ComingSoon) ?? false)
        );
    }, [data, params.showHidden]);

    return { data: filteredData, isLoading, isFetching: query.isFetching };
}

export function isLoopPrincipalWindDisabled(loopVault: LoopVaultInfo | undefined): boolean {
    return isLoopWindDisabled(loopVault, "principal");
}

export function isLoopCollateralWindDisabled(loopVault: LoopVaultInfo | undefined): boolean {
    return isLoopWindDisabled(loopVault, "collateral");
}

function isLoopWindDisabled(loopVault: LoopVaultInfo | undefined, windType: "collateral" | "principal"): boolean {
    return (
        loopVault?.metadata?.disable?.some(
            (entry) => typeof entry === "string" && entry.trim().toLowerCase() === windType
        ) ?? false
    );
}

export function getAllowedWindTypes(loopExpanded: LoopExpanded | undefined) {
    if (!loopExpanded) return undefined;
    const disablePrincipal = isLoopPrincipalWindDisabled(loopExpanded.loopVault);
    const disableCollateral = isLoopCollateralWindDisabled(loopExpanded.loopVault);

    if (loopExpanded.loopVault.routeType === LoopRouteType.Orca || disableCollateral) {
        if (disablePrincipal) return [];
        return [WindType.Principal];
    }
    if (disablePrincipal) return [WindType.Collateral];
    return [WindType.Collateral, WindType.Principal];
}

export function useFlashPerps(options?: { skip?: boolean }) {
    return useFlashPerpsQuery(undefined, { skip: options?.skip });
}

export function useUserLoopByLoan(loanExpanded: AbfLoanExpanded | undefined, options?: { skip?: boolean }) {
    const skip = !loanExpanded?.loanLoopVaultPairIdentifier || !isLoopLoan(loanExpanded) || options?.skip;
    const loopQuery = useLoopsExpanded(
        {
            loopVaults: loanExpanded?.loanLoopVaultPairIdentifier ? [loanExpanded.loanLoopVaultPairIdentifier] : [],
            showHidden: true
        },
        { skip }
    );

    const loopExpanded = loopQuery.data ? Object.values(loopQuery.data)[0] : undefined;

    const isLoading = loopQuery.isLoading;
    const isFetching = loopQuery.isFetching;

    const data = useMemo((): LoopPositionExpanded | undefined => {
        if (!loanExpanded || !loopExpanded || isLoading) return undefined;
        const allowedWindTypes = getAllowedWindTypes(loopExpanded);
        if (!allowedWindTypes) return undefined;

        return {
            loanExpanded,
            loopExpanded,
            allowedWindTypes
        };
    }, [loanExpanded, isLoading, loopExpanded]);

    return { data, isLoading, isFetching };
}

export function formatLeverage(leverage: number | undefined, decimals = 1) {
    return `${formatNum(leverage, { customDecimals: decimals })}x`;
}

export function getLoopWindType(loop: LoopVaultInfo) {
    if (isLoopCollateralWindDisabled(loop)) {
        return WindType.Principal;
    }
    if (isLoopPrincipalWindDisabled(loop)) {
        return WindType.Collateral;
    }
    if (loop.name.toLowerCase().includes("orca")) {
        return WindType.Principal;
    }
    return WindType.Collateral;
}

export function getWhirlpoolFromLoopVault(loopVault: LoopVaultInfo | undefined) {
    if (!loopVault) return undefined;
    if (loopVault.routeType !== LoopRouteType.Orca) return undefined;

    if (isWhirlpoolRouteParams(loopVault.quoteFetchParams)) {
        return loopVault.quoteFetchParams.whirlpool ?? loopVault.collateralMint;
    }

    return undefined;
}

export function useWindQuoteAndIxs({
    loopExpanded,
    principalAmount,
    slippagePercentDecimals,
    inputAmount,
    clmmPosition,
    inputToken,
    windMint,
    skip,
    userWallet,
    principalUsdPrice
}: ExternalWindQuoteParams) {
    // Minimal param validation: all API-required params must exist and be valid. We do not require loan address.
    const valid =
        !!loopExpanded &&
        !!userWallet &&
        typeof slippagePercentDecimals === "number" &&
        typeof principalAmount === "number" &&
        typeof inputAmount === "number" &&
        !skip;

    // Only construct request if valid, always call the query for cache consistency
    const request = useMemo(() => {
        if (!valid) return undefined;
        return buildWindQuoteRequest({
            loopExpanded,
            principalAmount,
            slippagePercentDecimals,
            inputAmount,
            clmmPosition,
            inputToken,
            windMint,
            userWallet,
            skip,
            principalUsdPrice
        });
    }, [
        valid,
        loopExpanded,
        principalAmount,
        slippagePercentDecimals,
        inputAmount,
        clmmPosition,
        inputToken,
        windMint,
        userWallet,
        skip,
        principalUsdPrice
    ]);

    // Always run the hook unless required params are missing (i.e., request is undefined)
    return useSwapInstructionsQuery(request ?? skipToken, {
        skip: !request
    });
}

export function usePandoraQuoteAndIxs(request: SwapParams | undefined) {
    return useSwapInstructionsQuery(request ?? skipToken, {
        skip: !request
    });
}

export function useMarketsQuoteAndIxs(request: VaultMintLpParams | undefined) {
    return useMarketsWindQuoteIxsQuery(request ?? skipToken, {
        skip: !request
    });
}

const UNWIND_REFETCH_INTERVAL = TIME.MINUTE * 1000;

export function useUnwindQuoteAndIxs({
    skip,
    slippagePercentDecimals,
    windType,
    isRollover,
    splitRolloverLegs,
    isCollateralSwap,
    loanCollateral,
    loanExpanded,
    debtToRepay,
    outputToken,
    collateralAmount, // have to specify the amount of collateral for repay with collateral cases. other callers will passing full collateral amount
    disablePoll = false
}: ExternalUnwindQuoteParams) {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();
    // Loop/loan actions always opt into Jito — tip-based prioritization + auto-bundling when the
    // response comes back multi-tx (via splitAtaCreates below + unifySetup: false downstream).
    const resolvedUseJito = true;

    const request = useMemo(() => {
        if (
            !loanExpanded ||
            !collateralAmount ||
            !loanCollateral ||
            !outputToken ||
            slippagePercentDecimals === undefined ||
            !activeWallet ||
            skip ||
            windType === undefined ||
            debtToRepay === undefined
        )
            return undefined;

        const collateralMetadata = loanCollateral?.metadata;
        const principalPrice = loanExpanded.principalUsdPrice;
        const collateralPrice = loanCollateral.usdPrice;

        // When receiving collateral, only swap the amount needed to repay the debt (with a slippage buffer);
        // otherwise swap the full amount.
        let collateralAmountToSwap = collateralAmount;
        if (
            windType === WindType.Collateral &&
            principalPrice &&
            collateralPrice &&
            collateralPrice > 0 &&
            debtToRepay !== undefined
        ) {
            const slippageMultiplier = 1 + slippagePercentDecimals + 0.005; // add an extra 1% buffer
            const collateralNeededForDebt = (debtToRepay * principalPrice * slippageMultiplier) / collateralPrice;

            if (collateralNeededForDebt > 0) {
                collateralAmountToSwap = Math.min(collateralAmountToSwap, collateralNeededForDebt);
            }
        }

        const collateralAmountLamports = uiAmountToLamports(collateralAmountToSwap, collateralMetadata.decimals);

        const wrapSol = loanCollateral?.assetMint === WRAPPED_SOL_MINT || outputToken.mint === WRAPPED_SOL_MINT;

        // `splitRolloverLegs` isn't in the published pandora-types yet.
        const optionalParams: SwapParams["optionalParams"] & { splitRolloverLegs?: boolean } = {
            loanAddress: loanExpanded.loan.address,
            wrapSol: wrapSol || undefined,
            isRollover: isRollover || undefined,
            splitRolloverLegs: (isRollover && splitRolloverLegs) || undefined,
            useJito: resolvedUseJito || undefined,
            minOutputAmount:
                isRollover || isCollateralSwap // min out is not needed for rollover its determined determined in pandora
                    ? undefined
                    : uiAmountToLamports(debtToRepay, outputToken.decimals) || undefined,
            // Always hoist ATA creates into setupIxs so downstream builders (swap_collateral,
            // flash_borrow) can emit a separate setup tx via unifySetup: false and Jito bundles them.
            splitAtaCreates: true
        };

        const request: SwapParams = {
            slippageBps: decimalsToBps(slippagePercentDecimals, "bps"),
            userWallet: activeWallet,
            inputMint: loanCollateral?.assetMint,
            outputMint: outputToken.mint,
            inputAmount: collateralAmountLamports - 1,
            optionalParams
        };
        return request;
    }, [
        slippagePercentDecimals,
        activeWallet,
        skip,
        windType,
        outputToken,
        isRollover,
        splitRolloverLegs,
        isCollateralSwap,
        loanExpanded,
        loanCollateral,
        debtToRepay,
        collateralAmount,
        resolvedUseJito
    ]);

    const swapInstructionsQuery = useSwapInstructionsQuery(request ?? skipToken, {
        skip: !request || skip,
        refetchOnMountOrArgChange: true,
        pollingInterval: disablePoll ? 0 : UNWIND_REFETCH_INTERVAL // 0 is default off per RTK Query docs
    });

    return swapInstructionsQuery;
}

export function calculateLtvFromLeverage(leverageMultiplier: number) {
    return 1 - 1 / leverageMultiplier;
}

export function getLoopLeveragedAmounts({
    inputToken,
    principalToken,
    collateralToken,
    leverageMultiplier,
    inputAmount,
    oracleQuotes,
    loopExpanded
}: {
    inputToken: TokenListMetadata;
    principalToken: TokenListMetadata;
    leverageMultiplier: number;
    inputAmount: number;
    oracleQuotes?: Map<string, OracleQuote | undefined>;
    collateralToken: TokenListMetadata;
    loopExpanded: LoopExpanded | undefined;
}) {
    const isOrcaRoute = loopExpanded?.loopVault.routeType === LoopRouteType.Orca;
    const isPrincipalWind = isPrincipalTokenWind(principalToken.mint, inputToken.mint) && !isOrcaRoute;
    const leveragedInputAmount = inputAmount * leverageMultiplier;
    const inputOracle = oracleQuotes?.get(inputToken.mint);
    const principalOracle = oracleQuotes?.get(principalToken.mint);
    const collateralOracle = oracleQuotes?.get(collateralToken.mint);

    const leveragedInputAmountUsd = inputOracle?.getUsdAmount(
        leveragedInputAmount,
        undefined,
        UncertaintyType.Subtract
    );

    if (leveragedInputAmountUsd === undefined || principalOracle === undefined) {
        return {
            principalAmount: 0,
            leveragedCollateralAmount: 0
        };
    }
    const principalUsd = leveragedInputAmountUsd * calculateLtvFromLeverage(leverageMultiplier);
    const principalAmount = roundDownToDecimals(
        principalUsd / (principalOracle.usdPrice + principalOracle.uncertainty),
        principalToken.decimals
    );

    const leveragedCollateralAmount = isPrincipalWind
        ? leveragedInputAmountUsd / (collateralOracle?.usdPrice ?? 0)
        : leveragedInputAmount;

    return {
        principalAmount,
        leveragedCollateralAmount
    };
}

type LoopBestQuoteParams = {
    loopExpanded: LoopExpanded | undefined;
    collateralAmountUi: number | undefined;
    inputAmountUi: number | undefined;
    inputToken: TokenListMetadata | undefined;
    leverageMultiplier: number | undefined;
    slippagePctDecimals?: number;
    priceOverride?: number;
    strategyDuration: StrategyDuration;
    skip?: boolean;
    oracleQuotes?: Map<string, OracleQuote | undefined>;
};

export function useLoopBestQuote(params: LoopBestQuoteParams) {
    const { convertOfferQuote } = useAbfTypesToUiConverter();
    const { pausedExternalYieldSources } = usePausedExternalYieldSources();

    const quoteParams = useMemo((): BestLoopQuoteQuery | undefined => {
        if (
            !params.loopExpanded ||
            !params.collateralAmountUi ||
            !isValidLeverage(params.leverageMultiplier) ||
            !params.strategyDuration
        )
            return undefined;
        return {
            principalMint: params.loopExpanded?.principalToken.mint,
            collateralInfo: {
                assetIdentifier: params.loopExpanded?.collateralToken.mint,
                amount: uiAmountToLamports(params.collateralAmountUi, params.loopExpanded?.collateralToken.decimals),
                assetMint: params.loopExpanded?.collateralToken.mint,
                assetType: params.loopExpanded?.collateralToken.assetType,
                priceOverride: params.priceOverride
            },
            duration: params.strategyDuration,
            leverageMultiplier: params.leverageMultiplier,
            excluded_external_yield: pausedExternalYieldSources
        };
    }, [params, pausedExternalYieldSources]);

    const skip = !quoteParams || params.skip;
    const {
        currentData: rawData,
        data: _staleData,
        ...query
    } = useLoopBestQuoteQuery(quoteParams ?? skipToken, { skip });

    const data = useMemo((): LoopQuoteExpanded | null | undefined => {
        if (!params.loopExpanded || !quoteParams || !params.collateralAmountUi || !params.inputAmountUi)
            return undefined;
        if (!rawData) return rawData;
        const collateralAmount = params.collateralAmountUi;
        const inputAmount = params.inputAmountUi;
        const bestQuote = convertOfferQuote(rawData.bestQuote, params.loopExpanded?.principalToken.mint);
        const { principalAmount, leveragedCollateralAmount } = getLoopLeveragedAmounts({
            principalToken: params.loopExpanded.principalToken,
            collateralToken: params.loopExpanded.collateralToken,
            inputToken: params.inputToken ?? params.loopExpanded.inputToken,
            leverageMultiplier: quoteParams.leverageMultiplier - (params.slippagePctDecimals ?? 0.05),
            inputAmount,
            oracleQuotes: params.oracleQuotes,
            loopExpanded: params.loopExpanded
        });
        return {
            ...rawData,
            netApyPct: percentUiToDecimals(rawData.netApyPct),
            bestQuote,
            leverageMultiplier: quoteParams.leverageMultiplier - (params.slippagePctDecimals ?? 0.05),
            collateralAmount,
            principalAmount,
            leveragedCollateralAmount
        };
    }, [quoteParams, rawData, convertOfferQuote, params]);

    return { ...query, data };
}

export function usePrincipalBorrowedWithLeverage({
    loopExpanded,
    loopLeverage,
    totalAmount,
    skip = false,
    oracleQuotes
}: {
    loopExpanded: LoopExpanded | undefined;
    loopLeverage: number | undefined;
    totalAmount: number;
    skip?: boolean;
    oracleQuotes?: Map<string, OracleQuote | undefined>;
}) {
    const quoteParams = useMemo((): LoopBestQuoteParams => {
        return {
            loopExpanded,
            leverageMultiplier: loopLeverage,
            inputAmountUi: totalAmount,
            collateralAmountUi: totalAmount,
            strategyDuration: DEFAULT_STRATEGY_DURATION,
            oracleQuotes,
            inputToken: loopExpanded?.inputToken
        };
    }, [loopExpanded, loopLeverage, totalAmount, oracleQuotes]);

    const { data: quote, isLoading: isQuoteLoading } = useLoopBestQuote({
        ...quoteParams,
        skip
    });

    const principalBorrowedWithLeverageUsd = useMemo(() => {
        const principalOracle = oracleQuotes?.get(loopExpanded?.principalToken.mint ?? "");
        if (!quote?.principalAmount || principalOracle?.usdPrice === undefined) {
            return undefined;
        }
        return quote.principalAmount * principalOracle.usdPrice;
    }, [quote, oracleQuotes, loopExpanded]);

    return {
        principalBorrowedWithLeverageUsd,
        isLoading: isQuoteLoading
    };
}

export function getDerivedLoopAssetMints(loopExpanded: LoopExpanded | undefined) {
    if (!loopExpanded) return undefined;
    return [loopExpanded.collateralToken.mint, loopExpanded.principalToken.mint];
}

export function getLoopBorrowCapDetails(loopExpanded: LoopExpanded | undefined) {
    if (!loopExpanded || !loopExpanded.borrowCap || !loopExpanded.principalStats) return undefined;
    return getBorrowCapDetails({
        borrowCap: loopExpanded.borrowCap,
        token: loopExpanded.principalToken,
        principalUtilized: loopExpanded.principalStats.principalUtilized
    });
}

// Backed by /loans/info with the Loop funding-type filter — loops are loans under the
// hood. The returned `aggregate` is a `LoansAggregate` scoped to loop-funding-type loans
// for the user; the section header composes loop-specific derivations (net position,
// equity-based net APY, pending yield) from those primitives.
export function useUserLoops({
    pagination,
    skip,
    loopVaultPrincipalCollateralPairs,
    narrowToCollateralIdentifiers
}: {
    pagination: LoanPaginationInfo | null;
    skip?: boolean;
    loopVaultPrincipalCollateralPairs?: PrincipalCollateralPair[];
    /** When set, `/loans/info` is restricted to these `loan_collateral.asset_identifier` values (OR). */
    narrowToCollateralIdentifiers?: readonly string[];
}): {
    data: LoopPositionExpanded[] | undefined;
    isLoading: boolean;
    isFetching: boolean;
    totalCount: number | undefined;
    aggregate: LoansAggregate | undefined;
    snapshotTs: number | undefined;
} {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const loopLoanFilter = useMemo((): LoanFilterApi => {
        return {
            loanAddresses: [],
            orderFundingTypes: AbfOrderFundingType.Loop,
            borrowers: activeWallet ? [activeWallet] : [],
            lenders: [],
            principalMints: [],
            sortSide: SortDirection.Asc,
            sortType: LoanInfoSorting.StartTime,
            collateralMints: [],
            collateralIdentifiers: narrowToCollateralIdentifiers?.length ? [...narrowToCollateralIdentifiers] : [],
            loopVaultPrincipalCollateralPairs: loopVaultPrincipalCollateralPairs ?? []
        };
    }, [activeWallet, narrowToCollateralIdentifiers, loopVaultPrincipalCollateralPairs]);

    const {
        data: loanInfos,
        isLoading: loanInfosLoading,
        isFetching: loanInfosFetching,
        totalLoans,
        aggregate,
        snapshotTs
    } = useLoanInfos({
        skip: skip || !activeWallet,
        pagination,
        loanFilter: loopLoanFilter
    });

    const loopAddresses = loanInfos?.map((l) => l.loanLoopVaultPairIdentifier).filter(filterTruthy);

    const {
        data: loopsExpanded,
        isLoading: isLoopExtendedLoading,
        isFetching: isLoopExtendedFetching
    } = useLoopsExpanded({ showHidden: true, loopVaults: loopAddresses ?? [] }, { skip });

    const loopGetKey = useCallback((l: LoopExpanded) => `${l.principalToken.mint}-${l.collateralToken.mint}`, []);
    const loopExpandedMap = useMemoizedKeyMap(loopsExpanded, loopGetKey);

    const isLoading = loanInfosLoading || isLoopExtendedLoading;
    const isFetching = loanInfosFetching || isLoopExtendedFetching;

    const data = useMemo(() => {
        if (isLoading || isFetching) return undefined;
        if (skip || !loanInfos || !loopsExpanded) return [];
        const data = loanInfos.map((loanExpanded): LoopPositionExpanded | undefined => {
            const loopExpanded = loopExpandedMap?.get(loanExpanded.loanLoopVaultPairIdentifier ?? "");
            return {
                loanExpanded,
                loopExpanded,
                allowedWindTypes: getAllowedWindTypes(loopExpanded) ?? []
            };
        });

        return data.filter(filterUndefined);
    }, [isLoading, loopsExpanded, isFetching, skip, loanInfos, loopExpandedMap]);

    return { data, isLoading, isFetching, totalCount: totalLoans, aggregate, snapshotTs };
}

export function useAllUserLoops({
    pagination,
    loopVaultPrincipalCollateralPairs
}: {
    pagination: LoanPaginationInfo;
    loopVaultPrincipalCollateralPairs?: PrincipalCollateralPair[];
}) {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    return useUserLoops({
        pagination,
        skip: !activeWallet,
        loopVaultPrincipalCollateralPairs
    });
}

/**
 * One batched `/loans/info` for the wallet's loop-funded positions (capped at {@link MAXIMUM_PAGE_SIZE}),
 * scoped on the server to visible groups' collateral identifiers, then grouped by {@link loanCollateralGroupTableKey}.
 * Pair with `useUserLoopGroups` (loop summaries) so inner rows do not paginate per expand — same pattern as
 * {@link useStrategiesGroupedByMint}.
 */
export function useUserLoopsGroupedByCollateralGroupKey(
    visibleGroupKeys: readonly string[],
    options?: { skip?: boolean }
): {
    loopsByGroupKey: Map<string, LoopPositionExpanded[]> | undefined;
    isLoading: boolean;
} {
    const {
        data: { userWalletAddress: activeWallet }
    } = useLoopscaleWalletContext();

    const pagination = useMemo((): LoanPaginationInfo => {
        return {
            loanStatusType: LoanStatusType.ActiveOnly,
            sortType: LoanInfoSorting.NetPositionValue,
            sortSide: SortDirection.Asc,
            principalMint: undefined,
            collateralMint: undefined,
            page: 0,
            pageSize: MAXIMUM_PAGE_SIZE
        };
    }, []);

    const narrowToCollateralIdentifiers = useMemo(
        () => uniqueCollateralIdentifiersFromLoanGroupKeys(visibleGroupKeys),
        [visibleGroupKeys]
    );

    const skipBase =
        options?.skip ||
        !activeWallet ||
        visibleGroupKeys.length === 0 ||
        (visibleGroupKeys.length > 0 && narrowToCollateralIdentifiers.length === 0);

    const {
        data: allPositions,
        isLoading,
        isFetching
    } = useUserLoops({
        pagination,
        skip: skipBase,
        narrowToCollateralIdentifiers
    });

    const filteredPositions = useMemo(() => {
        if (!allPositions || visibleGroupKeys.length === 0) return undefined;
        const allowed = new Set(visibleGroupKeys);
        return allPositions.filter((p) => {
            const k = loanCollateralGroupTableKey(p.loanExpanded);
            return k != null && allowed.has(k);
        });
    }, [allPositions, visibleGroupKeys]);

    const loopsByGroupKey = useMemoizedGroupedMap(filteredPositions, (p: LoopPositionExpanded) =>
        loanCollateralGroupTableKey(p.loanExpanded)
    );

    return {
        loopsByGroupKey,
        isLoading: isLoading || isFetching
    };
}

export function useUserLoopPositionsPaginated({
    pagination,
    collateralGroupKey,
    skip
}: {
    pagination: LoanPaginationInfo;
    collateralGroupKey: AllValueType | string | number;
    skip?: boolean;
}): {
    positions: LoopPositionExpanded[] | undefined;
    totalCount: number | undefined;
    isLoading: boolean;
    isFetching: boolean;
} {
    const narrowToCollateralIdentifiers = useMemo(() => {
        if (isAll(collateralGroupKey)) return undefined;
        return uniqueCollateralIdentifiersFromLoanGroupKeys([String(collateralGroupKey)]);
    }, [collateralGroupKey]);

    const { data, totalCount, isLoading, isFetching } = useUserLoops({
        pagination,
        skip,
        narrowToCollateralIdentifiers
    });

    return { positions: data, totalCount, isLoading, isFetching };
}

const TRENDING_LOOPS_LIMIT = 3;
const TRENDING_LOOPS_MIN_DEPOSIT_THRESHOLD = 25_000; // $15k minimum deposits to be displayed
export function useTrendingLoops() {
    const { data: loops } = useLoopsExpanded({
        showHidden: false
    });

    const trendingLoops: LoopExpanded[] | undefined = useMemo(() => {
        if (!loops) return undefined;

        return loops
            .filter((loop) => loop.loopVault.oneWeekCollateralDepositsUsd !== undefined)
            .filter((loop) => loop.loopVault.collateralDepositedUsd >= TRENDING_LOOPS_MIN_DEPOSIT_THRESHOLD)
            .sort(
                (a, b) =>
                    (b.loopVault.oneWeekCollateralDepositsUsd ?? 0) - (a.loopVault.oneWeekCollateralDepositsUsd ?? 0)
            )
            .slice(0, TRENDING_LOOPS_LIMIT);
    }, [loops]);

    return { data: trendingLoops, isLoading: false };
}

// leverage must be at least 1 to start a loan
export const isValidLeverage = (leverageMultiplier: number | undefined): leverageMultiplier is number =>
    greaterThan(leverageMultiplier, 1);

export interface UnwindTokenStats {
    balanceChanges: BalanceChanges | undefined;
    tokenPrices: Record<string, number> | undefined;
    receiveUsdAmount: number;
    receiveCollateralTokenAmount: number;
}

const LOAN_CLOSE_SOL_BALANCE = 14445852;

export function useUnwindStats({
    loopPosition,
    externalQuote,
    previewBalances
}: {
    loopPosition: LoopPositionExpanded | undefined;
    externalQuote: SwapInstructionsResponse | undefined;
    previewBalances: BalanceChanges | undefined;
}): UnwindTokenStats | undefined {
    const allMints = previewBalances ? Object.keys(previewBalances) : [];
    const { getMetadata } = useTokenListContext();
    const { getOracle } = useOraclePrices(allMints);

    if (!loopPosition || !externalQuote || !previewBalances) return undefined;

    const collateralMint = getZcCollateral(loopPosition.loanExpanded)?.assetIdentifier;
    const tokenPrices = Object.fromEntries(allMints.map((mint) => [mint, getOracle(mint)?.marketPrice ?? 0]));

    // divide amount with decimals and remove mints with no metadata
    // remove sol balance if < LOAN_CLOSE_SOL_BALANCE
    previewBalances =
        previewBalances &&
        Object.fromEntries(
            Object.entries(previewBalances)
                .map(([mint, amount]) => {
                    const metadata = getMetadata(mint);
                    return metadata ? [mint, lamportsToUiAmount(amount, metadata.decimals)] : null;
                })
                .filter((entry): entry is [string, number] => entry !== null)
        );

    if (
        previewBalances &&
        previewBalances[WRAPPED_SOL_MINT] <
            lamportsToUiAmount(LOAN_CLOSE_SOL_BALANCE, getMetadata(WRAPPED_SOL_MINT)?.decimals || 0)
    ) {
        delete previewBalances[WRAPPED_SOL_MINT];
    }

    const receiveUsdAmount = previewBalances
        ? Object.entries(previewBalances).reduce((sum, [mint, amount]) => {
              const price = tokenPrices[mint] ?? 0;
              return sum + amount * price;
          }, 0)
        : 0;

    const receiveCollateralTokenAmount = collateralMint ? previewBalances?.[collateralMint] : 0;

    const tokenStats: UnwindTokenStats = {
        balanceChanges: previewBalances,
        tokenPrices,
        receiveUsdAmount,
        receiveCollateralTokenAmount
    };

    return tokenStats;
}

interface TokenProfitLossValues {
    netCollateralTransfer: EventTokenChangeSummary | undefined;
    additionalBorrows: EventTokenChangeSummary | undefined;
    additionalRepayments: EventTokenChangeSummary | undefined;
    collateralPnl: number | undefined;
    valueOfAdditionalBorrows: number | undefined;
    valueOfAdditionalRepayments: number | undefined;
    valueOfNetCollateralTransfer: number | undefined;
    usdProfitLoss: number;
}

export function calculatePrincipalDebtTransfers(position: LoopPositionExpanded | undefined) {
    const additionalBorrows = calculateAdditionalBorrowsFromEvents(position?.loanExpanded.events);
    const additionalRepayments = calculateLoopRepayments(position?.loanExpanded.events);

    const valueOfAdditionalBorrows = bsMath.mul(
        additionalBorrows?.tokenAmount,
        additionalBorrows?.weightedAverageTokenAmountPrice
    );
    const valueOfAdditionalRepayments = bsMath.mul(
        additionalRepayments?.tokenAmount,
        additionalRepayments?.weightedAverageTokenAmountPrice
    );

    return {
        additionalBorrows,
        additionalRepayments,
        valueOfAdditionalBorrows,
        valueOfAdditionalRepayments,
        netPrincipalDebtTransferUsd: bsMath.sub(valueOfAdditionalBorrows, valueOfAdditionalRepayments),
        netPrincipalDebtTransferTokenAmount: bsMath.sub(
            additionalBorrows?.tokenAmount,
            additionalRepayments?.tokenAmount
        )
    };
}

export function calculateOracleProfitLossStats(
    position: LoopPositionExpanded | undefined,
    principalUsdOraclePrice: number | undefined,
    collateralUsdOraclePrice: number | undefined
): TokenProfitLossValues | undefined {
    if (!position) return undefined;
    const { loanExpanded } = position;
    const collateral = getZcCollateral(loanExpanded);
    if (!collateralUsdOraclePrice || !collateral) return undefined;
    const netCollateralTransfer = getNetCollateralTransfers(
        position,
        position.loanExpanded.events,
        position.loanExpanded.pnlStats?.initialUserCollateralContribution,
        principalUsdOraclePrice
    );

    const additionalBorrows = calculateAdditionalBorrowsFromEvents(position.loanExpanded.events);
    const additionalRepayments = calculateLoopRepayments(position.loanExpanded.events);

    const netPosition = getNetPosition(position, principalUsdOraclePrice, collateralUsdOraclePrice);

    const npvTokenAmount = netPosition?.tokenNpv;

    const inputPrice = collateralUsdOraclePrice;

    const fees = getClaimableWhirlpoolFees(getWhirlpoolLoopCollateral(position));

    return calculateOraclePnl({
        npvUsd: netPosition?.usdNpv ?? 0,
        npvTokenAmount,
        netCollateralTransfer,
        additionalBorrows,
        additionalRepayments,
        inputPrice,
        events: position.loanExpanded.events,
        pendingEarnedFeesUsd: fees
    });
}

function calculateOraclePnl({
    npvUsd,
    npvTokenAmount,
    netCollateralTransfer,
    additionalBorrows,
    additionalRepayments,
    inputPrice, // this is how we are pricing the collateral. orca collateral can be priced in both principal and collateral
    events,
    isPrincipalNpvToken,
    pendingEarnedFeesUsd
}: {
    npvUsd: number;
    npvTokenAmount: number | undefined;
    netCollateralTransfer: EventTokenChangeSummary | undefined;
    additionalBorrows: EventTokenChangeSummary | undefined;
    additionalRepayments: EventTokenChangeSummary | undefined;
    inputPrice: number | undefined;
    events: ParsedLoanEventExpanded[];
    isPrincipalNpvToken?: boolean;
    pendingEarnedFeesUsd?: number;
}) {
    const valueOfNetCollateralTransfer = bsMath.mul(
        netCollateralTransfer?.tokenAmount,
        netCollateralTransfer?.weightedAverageTokenAmountPrice
    );

    const valueOfAdditionalBorrows = bsMath.mul(
        additionalBorrows?.tokenAmount,
        additionalBorrows?.weightedAverageTokenAmountPrice
    );

    const valueOfAdditionalRepayments = bsMath.mul(
        additionalRepayments?.tokenAmount,
        additionalRepayments?.weightedAverageTokenAmountPrice
    );

    const netAdditionalPrincipalValue = bsMath.sub(valueOfAdditionalBorrows, valueOfAdditionalRepayments);

    if (inputPrice === undefined || inputPrice === 0) {
        return {
            netCollateralTransfer,
            additionalBorrows,
            additionalRepayments,
            collateralPnl: undefined,
            valueOfAdditionalBorrows,
            valueOfAdditionalRepayments,
            valueOfNetCollateralTransfer,
            usdProfitLoss: 0
        };
    }
    const collateralTokenAmountOfAdditionalBorrows = bsMath.div(netAdditionalPrincipalValue, inputPrice);
    const feesTokenAmount = bsMath.div(pendingEarnedFeesUsd, inputPrice);

    const lastCloseEvents = getLastCloseEvents(events);
    const lastRepayPrincipalEvent = lastCloseEvents?.lastRepayPrincipalEvent;

    const usdProfitLoss = bsMath.sub(bsMath.add(npvUsd, netAdditionalPrincipalValue), valueOfNetCollateralTransfer);
    const usdProfitLossWithFees = bsMath.add(usdProfitLoss, pendingEarnedFeesUsd ?? 0);

    // HISTORICAL ONLY npvTokenAmount can be in principal or collateral. so if we are subtracting net flows, we need to convert it all to dollars, and the back into the principal price at swap
    const collateralPnl = isPrincipalNpvToken
        ? bsMath.div(usdProfitLossWithFees, lastRepayPrincipalEvent?.usdPrice)
        : bsMath.sub(
              bsMath.add(bsMath.add(npvTokenAmount, feesTokenAmount ?? 0), collateralTokenAmountOfAdditionalBorrows),
              netCollateralTransfer?.tokenAmount
          );

    return {
        netCollateralTransfer,
        additionalBorrows,
        additionalRepayments,
        collateralPnl,
        valueOfAdditionalBorrows,
        valueOfAdditionalRepayments,
        valueOfNetCollateralTransfer,
        usdProfitLoss: usdProfitLossWithFees ?? 0
    };
}

export function isLstLoop(loop: LoopExpanded) {
    return loop.collateralToken.tags?.includes(TokenListTag.LST) && loop.principalToken.mint === WRAPPED_SOL_MINT;
}

export function isExponentLoop(loop: LoopExpanded) {
    return loop.loopVault.routeType === LoopRouteType.Exponent;
}

export function isRatexLoop(loop: LoopExpanded) {
    return loop.loopVault.routeType === LoopRouteType.Ratex;
}

export function isMeteora(loopExpanded: LoopExpanded): loopExpanded is LoopExpandedMeteora {
    return "meteoraPool" in loopExpanded && "tokenA" in loopExpanded && "tokenB" in loopExpanded;
}

export function isWhirlpoolLoop(loopExpanded: LoopExpanded | undefined): loopExpanded is LoopExpandedOrca {
    return !!loopExpanded && "whirlpool" in loopExpanded;
}

export function getWhirlpoolLoopCollateral(
    loopPosition: LoopPositionExpanded | undefined
): WhirlpoolPositionExpanded | undefined {
    if (
        loopPosition &&
        isWhirlpoolLoop(loopPosition.loopExpanded) &&
        loopPosition.loanExpanded.collateral.length > 0 &&
        loopPosition.loanExpanded.collateral[0].clmmPosition
    ) {
        return loopPosition.loanExpanded.collateral[0].clmmPosition as WhirlpoolPositionExpanded;
    }
    return undefined;
}

interface OrcaCollateralConversion {
    collateralCTokenInUsd: number | undefined;
    collateralPTokenInUsd: number | undefined;
    totalCollateralInUsd: number | undefined;
    collateralCTokenInPrincipalToken: number | undefined;
    totalCollateralInPrincipalToken: number | undefined;
    collateralCTokenAmount: number | undefined;
    collateralPTokenAmount: number | undefined;
    collateralCTokenMetadata: TokenListMetadata | undefined;
    collateralPTokenMetadata: TokenListMetadata | undefined;
    initialCollateralAmount: number | undefined;
    totalInitialized: number | undefined;
}

export function orcaCollateralPrincipalInfo(
    loopPosition: LoopPositionExpanded | undefined
): OrcaCollateralConversion | undefined {
    if (!loopPosition) return undefined;
    if (!isWhirlpoolLoop(loopPosition.loopExpanded)) return undefined;

    const [collateralPToken, collateralCToken] = getOrcaCollateralTokenAmounts(loopPosition);

    const collateralCTokenInUsd = bsMath.mul(collateralCToken?.amount, collateralCToken?.usdPrice);
    const collateralPTokenInUsd = bsMath.mul(collateralPToken?.amount, collateralPToken?.usdPrice);
    const totalCollateralInUsd = bsMath.add(collateralCTokenInUsd, collateralPTokenInUsd);

    const { leveragedAmount, initialDepositAmount } = getInitialLoopDepositInfo(loopPosition);

    // convert collateral to principal
    const collateralCTokenInPrincipalToken = bsMath.div(collateralCTokenInUsd, collateralPToken?.usdPrice);
    const totalCollateralInPrincipalToken = bsMath.add(collateralCTokenInPrincipalToken, collateralPToken?.amount);

    const getAdditionalCollateralDeposits = calculateAdditionalCollateralDeposits(
        loopPosition.loanExpanded.events,
        loopPosition
    );

    const withdrawals = calculateWithdrawalsFromEvents(loopPosition.loanExpanded.events, loopPosition);

    const totalInitialized = bsMath.add(
        bsMath.add(leveragedAmount, initialDepositAmount),
        bsMath.sub(getAdditionalCollateralDeposits?.tokenAmount ?? 0, withdrawals?.tokenAmount ?? 0)
    );

    return {
        collateralCTokenInUsd,
        collateralPTokenInUsd,
        totalCollateralInUsd,
        collateralCTokenInPrincipalToken,
        totalCollateralInPrincipalToken,
        collateralCTokenAmount: collateralCToken?.amount,
        collateralPTokenAmount: collateralPToken?.amount,
        collateralCTokenMetadata: collateralCToken?.metadata,
        collateralPTokenMetadata: collateralPToken?.metadata,
        initialCollateralAmount: loopPosition.loanExpanded.pnlStats?.initialUserCollateralContribution,
        totalInitialized
    };
}

export function useLatestCollateralYieldHistoryByMint(collateralMints: string[] | undefined) {
    return useLoopCollateralYieldLatestByMintsQuery(collateralMints?.length ? collateralMints : skipToken);
}

/**
 * Latest collateral yields for ALL mints. Callers wanting the full principal-mint universe
 * should use this (rather than passing an explicit list) so they share the single cache entry.
 */
export function useAllLatestCollateralYields() {
    return useLoopCollateralYieldLatestByMintsQuery(ALL_MINTS);
}

export function useCalculateLstBreakevenTime({
    totalCollateralUsd,
    totalDebtUsd,
    feesUsd,
    lstApy,
    loanApy
}: LstBreakevenParams): number | undefined {
    return useMemo(() => {
        if (
            totalCollateralUsd === undefined ||
            totalDebtUsd === undefined ||
            feesUsd === undefined ||
            lstApy === undefined ||
            loanApy === undefined ||
            lstApy <= loanApy
        ) {
            return undefined;
        }

        // Approximate Solana epoch = 2 days (172,800 seconds)
        const epochDurationSeconds = 2 * TIME.DAY;

        const epochsPerYear = TIME.YEAR / epochDurationSeconds;
        const epochStakeRate = lstApy / epochsPerYear;
        const dailyDebtRate = loanApy / TIME_IN_DAYS.YEAR;

        let elapsed = 0; // seconds
        const maxDuration = TIME.MONTH * 3; // cap ~90 days

        while (elapsed < maxDuration) {
            const days = elapsed / TIME.DAY;
            const epochs = Math.floor(elapsed / epochDurationSeconds);

            // staking compounds per epoch
            const reward = totalCollateralUsd * (Math.pow(1 + epochStakeRate, epochs) - 1);

            // debt compounds daily
            const debtGrowth = totalDebtUsd * (Math.pow(1 + dailyDebtRate, days) - 1);

            if (reward >= debtGrowth + feesUsd) {
                return elapsed; // seconds
            }

            elapsed += TIME.DAY; // simulate day by day
        }

        return Number.MAX_SAFE_INTEGER; // no breakeven in 3 months
    }, [totalCollateralUsd, totalDebtUsd, feesUsd, lstApy, loanApy]);
}

export function useCalculatePtBreakevenTime({
    totalCollateralUsd,
    totalDebtUsd,
    feesUsd,
    ptApy,
    loanApy
}: PtBreakevenParams): number | undefined {
    return useMemo(() => {
        if (
            totalCollateralUsd === undefined ||
            totalDebtUsd === undefined ||
            feesUsd === undefined ||
            ptApy === undefined ||
            loanApy === undefined
        ) {
            return undefined;
        }

        const daysPerYear = TIME.YEAR / TIME.DAY;

        const dailyPtRate = Math.pow(1 + ptApy, 1 / daysPerYear) - 1;
        const dailyLoanRate = Math.pow(1 + loanApy, 1 / daysPerYear) - 1;

        let elapsed = 0;
        const maxDuration = TIME.YEAR * 3; // cap ~3 years

        let reward = 0;
        let debtGrowth = 0;

        while (elapsed < maxDuration) {
            const days = elapsed / TIME.DAY;

            reward = totalCollateralUsd * (Math.pow(1 + dailyPtRate, days) - 1);
            debtGrowth = totalDebtUsd * (Math.pow(1 + dailyLoanRate, days) - 1);

            if (reward >= debtGrowth + feesUsd) {
                return elapsed; // seconds
            }

            elapsed += TIME.DAY; // simulate day by day
        }

        return Number.MAX_SAFE_INTEGER;
    }, [totalCollateralUsd, totalDebtUsd, feesUsd, ptApy, loanApy]);
}
export interface EventTokenChangeSummary {
    tokenAmount: number;
    tokenAmountUsd: number;
    weightedAverageTokenAmountPrice: number;
}

function convertCollateralToPrincipalTokenChangeEvent(
    collateralTokenChange: EventTokenChangeSummary | undefined,
    principalTokenChange: EventTokenChangeSummary | undefined
): EventTokenChangeSummary | undefined {
    // Convert collateral token amounts to principal token equivalents
    const collateralTokenInPrincipalToken = bsMath.div(
        collateralTokenChange?.tokenAmount ?? 0,
        principalTokenChange?.weightedAverageTokenAmountPrice ?? 1
    );

    const collateralTokenInPrincipalTokenUsd = bsMath.div(
        collateralTokenChange?.tokenAmountUsd ?? 0,
        principalTokenChange?.weightedAverageTokenAmountPrice ?? 1
    );

    // Combine the stats into one unified EventTokenChangeSummary
    const combinedTokenChangeInPrincipal: EventTokenChangeSummary = {
        tokenAmount: bsMath.add(principalTokenChange?.tokenAmount, collateralTokenInPrincipalToken) ?? 0,
        tokenAmountUsd: bsMath.add(principalTokenChange?.tokenAmountUsd, collateralTokenInPrincipalTokenUsd) ?? 0,
        weightedAverageTokenAmountPrice:
            bsMath.div(
                bsMath.add(principalTokenChange?.tokenAmountUsd, collateralTokenInPrincipalTokenUsd),
                bsMath.add(principalTokenChange?.tokenAmount, collateralTokenInPrincipalToken)
            ) ?? 0
    };

    return combinedTokenChangeInPrincipal;
}

function calculateOrcaWithdrawalsFromEvents(
    events: ParsedLoanEventExpanded[] | undefined,
    position: LoopPositionExpanded | undefined
): EventTokenChangeSummary | undefined {
    const [collateralPToken, collateralCToken] = getOrcaCollateralTokens(position);

    // Aggregate the token amount changes for each collateral type
    const removePCollateralEvents = events?.filter(
        (e) =>
            isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.RemoveCollateral) &&
            e.assetIdentifier === collateralPToken?.mint
    );
    const removeCCollateralEvents = events?.filter(
        (e) =>
            isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.RemoveCollateral) &&
            e.assetIdentifier === collateralCToken?.mint
    );

    const removePCollateralTokenChange = calculateTokenChangesFromEvents(
        removePCollateralEvents,
        DerivedLoanEventActionType.RemoveCollateral
    );
    const removeCCollateralTokenChange = calculateTokenChangesFromEvents(
        removeCCollateralEvents,
        DerivedLoanEventActionType.RemoveCollateral
    );

    // Use the utility function to convert and combine the token changes
    return convertCollateralToPrincipalTokenChangeEvent(removeCCollateralTokenChange, removePCollateralTokenChange);
}

export function calculateWithdrawalsFromEvents(
    events: ParsedLoanEventExpanded[] | undefined,
    position: LoopPositionExpanded | undefined
): EventTokenChangeSummary | undefined {
    const isOrca = isWhirlpoolLoop(position?.loopExpanded);
    if (isOrca) {
        return calculateOrcaWithdrawalsFromEvents(events, position);
    }

    return calculateTokenChangesFromEvents(events, DerivedLoanEventActionType.RemoveCollateral);
}

export function calculateAdditionalBorrowsFromEvents(
    events: ParsedLoanEventExpanded[] | undefined
): EventTokenChangeSummary | undefined {
    return calculateTokenChangesFromEvents(events, DerivedLoanEventActionType.BorrowPrincipal, {
        skipFirstEvent: true
    });
}

export function calculateLoopRepayments(
    events: ParsedLoanEventExpanded[] | undefined,
    isHistorical = false
): EventTokenChangeSummary | undefined {
    return calculateTokenChangesFromEvents(events, DerivedLoanEventActionType.RepayPrincipal, {
        skipLastEvent: isHistorical
    });
}

export function getOrcaCollateralTokens(position: LoopPositionExpanded | undefined) {
    if (!isWhirlpoolLoop(position?.loopExpanded)) {
        return [undefined, undefined];
    }

    const tokenA = position?.loopExpanded?.whirlpool.tokenA;
    const tokenB = position?.loopExpanded?.whirlpool.tokenB;

    return tokenA?.mint === position?.loopExpanded?.principalToken.mint ? [tokenA, tokenB] : [tokenB, tokenA];
}

export function getOrcaCollateralTokenAmounts(position: LoopPositionExpanded | undefined) {
    const loopCollateral = getWhirlpoolLoopCollateral(position);

    const [collateralPToken, collateralCToken] =
        loopCollateral?.tokenA.mint === position?.loopExpanded?.principalToken.mint
            ? [loopCollateral?.tokenA, loopCollateral?.tokenB]
            : [loopCollateral?.tokenB, loopCollateral?.tokenA];

    return [collateralPToken, collateralCToken];
}

function calculateOrcaAdditionalCollateralDeposits(
    events: ParsedLoanEventExpanded[] | undefined,
    position: LoopPositionExpanded | undefined
): EventTokenChangeSummary | undefined {
    const [collateralPToken, collateralCToken] = getOrcaCollateralTokenAmounts(position);
    if (!collateralPToken || !collateralCToken) {
        return undefined;
    }

    const addPCollateralEvents = events?.filter(
        (e) =>
            isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.AddCollateral) &&
            e.assetIdentifier === collateralPToken?.mint
    );
    const addCCollateralEvents = events?.filter(
        (e) =>
            isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.AddCollateral) &&
            e.assetIdentifier === collateralCToken?.mint
    );

    const addPCollateralTokenChange = calculateTokenChangesFromEvents(
        addPCollateralEvents,
        DerivedLoanEventActionType.AddCollateral
    );

    const addCCollateralTokenChange = calculateTokenChangesFromEvents(
        addCCollateralEvents,
        DerivedLoanEventActionType.AddCollateral
    );

    // Use the utility function to convert and combine the token changes
    return convertCollateralToPrincipalTokenChangeEvent(addCCollateralTokenChange, addPCollateralTokenChange);
}

// Skip first add collateral event - it's the loop start (initial + leveraged amounts), not an additional deposit
export function calculateAdditionalCollateralDeposits(
    events: ParsedLoanEventExpanded[] | undefined,
    position: LoopPositionExpanded | undefined
): EventTokenChangeSummary | undefined {
    const isOrca = isWhirlpoolLoop(position?.loopExpanded);
    if (isOrca) {
        const res = calculateOrcaAdditionalCollateralDeposits(events, position);
        return res;
    }

    return calculateTokenChangesFromEvents(events, DerivedLoanEventActionType.AddCollateral, { skipFirstEvent: true });
}

export interface NetCollateralTransfers {
    tokenAmount: number;
    tokenAmountUsd: number;
    weightedAverageTokenAmountPrice: number;
}

export interface NetOrcaCollateralTransfers extends NetCollateralTransfers {
    weightedAverageCollateralTokenAmountPrice: number;
    weightedAveragePrincipalTokenAmountPrice: number;
    collateralTokenAmount: number;
    principalTokenAmount: number;
    collateralTokenAmountUsd: number;
    principalTokenAmountUsd: number;
    principalUsdPrice: number;
}

export function getOrcaNetCollateralTransfersfromEvents({
    events,
    initialCollateralAmount,
    loopPositionExpanded,
    principalUsdPrice
}: {
    events: ParsedLoanEventExpanded[] | undefined;
    initialCollateralAmount: number | undefined;
    loopPositionExpanded: LoopPositionExpanded | undefined;
    principalUsdPrice: number | undefined;
}): NetOrcaCollateralTransfers | undefined {
    if (!events) return undefined;

    const [collateralPToken, collateralCToken] = getOrcaCollateralTokens(loopPositionExpanded);

    const sortedEvents = [...(events ?? [])].sort((a, b) => a.eventTime - b.eventTime);
    const firstBorrowEvent = sortedEvents.find((e) =>
        isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.BorrowPrincipal)
    ); // used for principal price;
    const filteredEvents = sortedEvents.filter(
        (e) =>
            (isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.AddCollateral) ||
                isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.RemoveCollateral)) &&
            (e.assetIdentifier === collateralPToken?.mint || e.assetIdentifier === collateralCToken?.mint)
    );

    const totalEvents = filteredEvents.reduce(
        (acc, event) => {
            const isPrincipalMint = event.eventAssetMetadata?.mint === collateralPToken?.mint;

            const tokenAmount = event.amount ?? 0;
            const eventPrice = event.usdPrice ?? 0;
            const tokenAmountUsd = bsMath.mul(tokenAmount, eventPrice) ?? 0;
            const tokenAmountNormlizeToPrincipal = bsMath.div(tokenAmountUsd, principalUsdPrice) ?? 0;

            if (isEventActionMetadataType(event.actionMetadata, DerivedLoanEventActionType.AddCollateral)) {
                return {
                    tokenAmount: acc.tokenAmount + tokenAmountNormlizeToPrincipal,
                    tokenAmountUsd: acc.tokenAmountUsd + tokenAmountUsd,
                    weightedAverageTokenAmountPrice: 0,
                    weightedAverageCollateralTokenAmountPrice: 0,
                    weightedAveragePrincipalTokenAmountPrice: 0,
                    collateralTokenAmount: isPrincipalMint
                        ? acc.collateralTokenAmount
                        : acc.collateralTokenAmount + tokenAmount,
                    principalTokenAmount: isPrincipalMint
                        ? acc.principalTokenAmount + tokenAmountNormlizeToPrincipal
                        : acc.principalTokenAmount,
                    collateralTokenAmountUsd: isPrincipalMint
                        ? acc.collateralTokenAmountUsd
                        : acc.collateralTokenAmountUsd + tokenAmountUsd,
                    principalTokenAmountUsd: isPrincipalMint
                        ? acc.principalTokenAmountUsd + tokenAmountUsd
                        : acc.principalTokenAmountUsd
                };
            } else {
                return {
                    tokenAmount: acc.tokenAmount - tokenAmountNormlizeToPrincipal,
                    tokenAmountUsd: acc.tokenAmountUsd - tokenAmountUsd,
                    weightedAverageTokenAmountPrice: 0,
                    weightedAverageCollateralTokenAmountPrice: 0,
                    weightedAveragePrincipalTokenAmountPrice: 0,
                    collateralTokenAmount: isPrincipalMint
                        ? acc.collateralTokenAmount
                        : acc.collateralTokenAmount - tokenAmount,
                    principalTokenAmount: isPrincipalMint
                        ? acc.principalTokenAmount - tokenAmountNormlizeToPrincipal
                        : acc.principalTokenAmount,
                    collateralTokenAmountUsd: isPrincipalMint
                        ? acc.collateralTokenAmountUsd
                        : acc.collateralTokenAmountUsd - tokenAmountUsd,
                    principalTokenAmountUsd: isPrincipalMint
                        ? acc.principalTokenAmountUsd - tokenAmountUsd
                        : acc.principalTokenAmountUsd
                };
            }
        },
        {
            tokenAmount: 0,
            tokenAmountUsd: 0,
            weightedAverageTokenAmountPrice: 0,
            weightedAverageCollateralTokenAmountPrice: 0,
            weightedAveragePrincipalTokenAmountPrice: 0,
            collateralTokenAmount: 0,
            principalTokenAmount: 0,
            collateralTokenAmountUsd: 0,
            principalTokenAmountUsd: 0
        }
    );

    totalEvents.principalTokenAmount += initialCollateralAmount ?? 0;
    totalEvents.principalTokenAmountUsd += bsMath.mul(firstBorrowEvent?.usdPrice, initialCollateralAmount) ?? 0;

    totalEvents.tokenAmount = bsMath.add(initialCollateralAmount, totalEvents.tokenAmount) ?? 0;
    totalEvents.tokenAmountUsd += bsMath.mul(firstBorrowEvent?.usdPrice, initialCollateralAmount) ?? 0;

    if (totalEvents.tokenAmountUsd > 0) {
        totalEvents.weightedAverageTokenAmountPrice = totalEvents.tokenAmountUsd / totalEvents.tokenAmount;
        totalEvents.weightedAverageCollateralTokenAmountPrice =
            bsMath.div(totalEvents.collateralTokenAmountUsd, totalEvents.collateralTokenAmount) ?? 0;
        totalEvents.weightedAveragePrincipalTokenAmountPrice =
            bsMath.div(totalEvents.principalTokenAmountUsd, totalEvents.principalTokenAmount) ?? 0;
    }
    return {
        ...totalEvents,
        principalUsdPrice: principalUsdPrice ?? 0
    };
}

export function getNetCollateralTransfers(
    loopPositionExpanded: LoopPositionExpanded | undefined,
    events: ParsedLoanEventExpanded[] | undefined, // we have events as an arg because we sometime pass in time filtered events for the charts
    initialCollateralAmount: number | undefined,
    principalUsdPrice: number | undefined,
    isHistorical = false
): NetCollateralTransfers | NetOrcaCollateralTransfers | undefined {
    if (!loopPositionExpanded) return undefined;
    if (isWhirlpoolLoop(loopPositionExpanded.loopExpanded)) {
        return getOrcaNetCollateralTransfersfromEvents({
            events,
            initialCollateralAmount,
            loopPositionExpanded,
            principalUsdPrice
        });
    }
    return getNetCollateralTransfersfromEvents(events, initialCollateralAmount, isHistorical);
}

function getNetCollateralTransfersfromEvents(
    events: ParsedLoanEventExpanded[] | undefined,
    initialCollateralAmount: number | undefined,
    isHistorical = false
): NetCollateralTransfers | undefined {
    if (!events) return undefined;

    const sortedEvents = [...events].sort((a, b) => a.eventTime - b.eventTime);
    const filteredEvents = sortedEvents.filter(
        (e) =>
            isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.AddCollateral) ||
            isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.RemoveCollateral)
    );

    // If historical, exclude the last remove collateral event
    if (isHistorical) {
        const lastRemoveIndex = [...filteredEvents]
            .reverse()
            .findIndex((e) => isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.RemoveCollateral));
        if (lastRemoveIndex !== -1) {
            filteredEvents.splice(filteredEvents.length - 1 - lastRemoveIndex, 1);
        }
    }

    const firstAddCollateralEventIndex = filteredEvents.findIndex((e) =>
        isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.AddCollateral)
    );
    const firstRemovedCollateralEvent =
        firstAddCollateralEventIndex !== -1 ? filteredEvents.splice(firstAddCollateralEventIndex, 1)[0] : undefined;

    const totalEvents = filteredEvents.reduce(
        (acc, event) => {
            const tokenAmount = event.amount ?? 0;
            const eventPrice = event.usdPrice ?? 0;
            const tokenAmountUsd = bsMath.mul(tokenAmount, eventPrice) ?? 0;

            if (isEventActionMetadataType(event.actionMetadata, DerivedLoanEventActionType.AddCollateral)) {
                return {
                    tokenAmount: acc.tokenAmount + tokenAmount,
                    tokenAmountUsd: acc.tokenAmountUsd + tokenAmountUsd,
                    weightedAverageTokenAmountPrice: 0
                };
            } else {
                return {
                    tokenAmount: acc.tokenAmount - tokenAmount,
                    tokenAmountUsd: acc.tokenAmountUsd - tokenAmountUsd,
                    weightedAverageTokenAmountPrice: 0
                };
            }
        },
        { tokenAmount: 0, tokenAmountUsd: 0, weightedAverageTokenAmountPrice: 0 }
    );

    totalEvents.tokenAmount = bsMath.add(initialCollateralAmount, totalEvents.tokenAmount) ?? 0;
    totalEvents.tokenAmountUsd += bsMath.mul(firstRemovedCollateralEvent?.usdPrice, initialCollateralAmount) ?? 0;

    if (totalEvents.tokenAmountUsd > 0) {
        totalEvents.weightedAverageTokenAmountPrice = totalEvents.tokenAmountUsd / totalEvents.tokenAmount;
    }

    return totalEvents;
}

export function calculateTokenChangesFromEvents(
    events: ParsedLoanEventExpanded[] | undefined,
    actionType: DerivedLoanEventActionType,
    skipOptions: { skipFirstEvent?: boolean; skipLastEvent?: boolean } = { skipFirstEvent: false, skipLastEvent: false }
    // for some callers need to skip the first or last event
    // either for manual calculations or historical calculations
): EventTokenChangeSummary | undefined {
    if (!events) return undefined;

    const sortedEvents = [...events].sort((a, b) => a.eventTime - b.eventTime);
    const filteredEvents = sortedEvents.filter((e) => isEventActionMetadataType(e.actionMetadata, actionType));

    const totalEvents = filteredEvents.reduce(
        (acc, event, index) => {
            if (skipOptions.skipFirstEvent && index === 0) {
                return acc;
            }
            if (skipOptions.skipLastEvent && index === filteredEvents.length - 1) {
                return acc;
            }
            const tokenAmount = event.amount ?? 0;
            const tokenPrice = event.usdPrice ?? 0;
            const tokenAmountUsd = event.amount * tokenPrice;

            return {
                tokenAmount: acc.tokenAmount + tokenAmount,
                tokenAmountUsd: acc.tokenAmountUsd + tokenAmountUsd,
                weightedAverageTokenAmountPrice: 0
            };
        },
        { tokenAmount: 0, tokenAmountUsd: 0, weightedAverageTokenAmountPrice: 0 }
    );

    if (totalEvents.tokenAmountUsd > 0) {
        totalEvents.weightedAverageTokenAmountPrice = totalEvents.tokenAmountUsd / totalEvents.tokenAmount;
    }

    return totalEvents;
}

function generatePrincipalLiquidatedInfo(event: ParsedLoanEventExpanded): number {
    if (
        isEventActionMetadataType(event.actionMetadata, DerivedLoanEventActionType.FullLiquidation) ||
        isEventActionMetadataType(event.actionMetadata, DerivedLoanEventActionType.PartialLiquidation)
    ) {
        const { repaymentEventMetadata } = event.actionMetadata.params;
        if (
            repaymentEventMetadata &&
            repaymentEventMetadata.preRepaymentPrincipalLedgerState &&
            repaymentEventMetadata.postRepaymentPrincipalLedgerState
        ) {
            const { preRepaymentPrincipalLedgerState, postRepaymentPrincipalLedgerState } = repaymentEventMetadata;
            const principalDelta = Math.abs(
                postRepaymentPrincipalLedgerState.principalRepaid - preRepaymentPrincipalLedgerState.principalRepaid
            );
            return principalDelta;
        }
    }
    return 0;
}

export function getLoopFirstAddCollateralEvent( // audit to make sure this works for orca
    events: ParsedLoanEventExpanded[] | undefined
): ParsedLoanEventExpanded | undefined {
    const sortedEvents = events?.sort((a, b) => a.eventTime - b.eventTime);
    const depositEvent = sortedEvents?.find((e) =>
        isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.AddCollateral)
    );
    return depositEvent;
}
export function getLoopFirstBorrowEvent(
    events: ParsedLoanEventExpanded[] | undefined
): ParsedLoanEventExpanded | undefined {
    const sortedEvents = events?.sort((a, b) => a.eventTime - b.eventTime);
    const depositEvent = sortedEvents?.find((e) =>
        isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.BorrowPrincipal)
    );
    return depositEvent;
}
interface TotalLoopLiquidationSummary {
    collateralLiquidated: number;
    collateralLiquidatedUsd: number;
    averageCollateralPrice: number;
    principalRepaidFromLiquidation: number;
    principalAmountUsd: number;
    averagePrincipalPrice: number;
}

export function calculateTotalLiquidations(
    events: ParsedLoanEventExpanded[] | undefined
): TotalLoopLiquidationSummary | undefined {
    const liquidationEvents = events?.filter((e) =>
        isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.Liquidate)
    );
    const partialLiquidationEvents = liquidationEvents?.filter((e) =>
        isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.PartialLiquidation)
    );
    const totalLiquidations = partialLiquidationEvents?.reduce(
        (acc, event) => {
            if (!isEventActionMetadataType(event.actionMetadata, DerivedLoanEventActionType.PartialLiquidation))
                return acc;
            const collateralAmount = event.actionMetadata.params.collateralTransfers[0].amount;
            const collateralPrice = event.actionMetadata.params.collateralTransfers[0].price;
            const collateralAmountUsd = collateralPrice * collateralAmount;
            const principalRepaidFromLiquidation = generatePrincipalLiquidatedInfo(event);
            const principalPriceLiquidated = event.usdPrice ?? 0;
            const principalAmountUsd = principalRepaidFromLiquidation * principalPriceLiquidated;
            return {
                collateralLiquidated: acc.collateralLiquidated + collateralAmount,
                collateralLiquidatedUsd: acc.collateralLiquidatedUsd + collateralAmountUsd,
                averageCollateralPrice: 0,
                principalRepaidFromLiquidation: acc.principalRepaidFromLiquidation + principalRepaidFromLiquidation,
                principalAmountUsd: acc.principalAmountUsd + principalAmountUsd,
                averagePrincipalPrice: 0
            };
        },
        {
            collateralLiquidated: 0,
            collateralLiquidatedUsd: 0,
            averageCollateralPrice: 0,
            principalRepaidFromLiquidation: 0,
            principalAmountUsd: 0,
            averagePrincipalPrice: 0
        }
    );
    if (totalLiquidations && totalLiquidations.collateralLiquidated > 0) {
        totalLiquidations.averageCollateralPrice =
            totalLiquidations.collateralLiquidatedUsd / totalLiquidations.collateralLiquidated;
    }
    if (totalLiquidations && totalLiquidations.principalRepaidFromLiquidation > 0) {
        totalLiquidations.averagePrincipalPrice =
            totalLiquidations.principalAmountUsd / totalLiquidations.principalRepaidFromLiquidation;
    }
    return totalLiquidations;
}

export function getInitialLoopDepositInfo(loopPositionExpanded: LoopPositionExpanded) {
    const isWhirlpool = isWhirlpoolLoop(loopPositionExpanded.loopExpanded);

    const collateralMetadata = loopPositionExpanded.loopExpanded?.inputToken;

    const initialDepositAmount = loopPositionExpanded.loanExpanded?.pnlStats?.initialUserCollateralContribution;

    const initalAddCollateralEvent = getLoopFirstAddCollateralEvent(loopPositionExpanded.loanExpanded.events);
    const firstBorrowEvent = getLoopFirstBorrowEvent(loopPositionExpanded.loanExpanded.events);

    if (!isWhirlpool) {
        // Calculate leveraged collateral swap price
        let leveragedCollateralSwapPrice: number | undefined;
        if (firstBorrowEvent && initalAddCollateralEvent) {
            const borrowedAmount = firstBorrowEvent.amount;
            const swappedCollateralAmount = bsMath.sub(initalAddCollateralEvent.amount, initialDepositAmount ?? 0);
            const usdSpent = bsMath.mul(borrowedAmount, firstBorrowEvent.usdPrice);
            leveragedCollateralSwapPrice = bsMath.div(usdSpent, swappedCollateralAmount);
        }

        const initialDepositPrice = initalAddCollateralEvent?.usdPrice;
        const leveragedAmount = bsMath.sub(initalAddCollateralEvent?.amount, initialDepositAmount);

        return {
            leveragedAmount,
            leveragedPrice: leveragedCollateralSwapPrice,
            initialDepositAmount,
            initialDepositAmountUsd: bsMath.mul(initialDepositAmount, initialDepositPrice),
            collateralMetadata,
            initialDepositPrice
        };
    }

    const firstBorrowPrincipalPrice = firstBorrowEvent?.usdPrice;

    return {
        leveragedAmount: firstBorrowEvent?.amount,
        leveragedPrice: firstBorrowPrincipalPrice,
        initialDepositAmount,
        collateralMetadata,
        initialDepositPrice: firstBorrowPrincipalPrice,
        initialDepositAmountUsd: bsMath.mul(initialDepositAmount, firstBorrowPrincipalPrice)
    };
}

export function getLoopExpandedPrincipalMint(loopPositionExpanded: LoopExpanded | undefined): string | undefined {
    if (!loopPositionExpanded) return undefined;
    return loopPositionExpanded.principalToken.mint;
}

export function getLoopExpandedCollateralMint(loopPositionExpanded: LoopExpanded | undefined): string | undefined {
    if (!loopPositionExpanded) return undefined;
    return loopPositionExpanded.collateralToken.mint;
}

/**
 * Get principal and collateral oracles from a pre-fetched oracle prices map.
 * Use this when you already have oraclePrices from context. For fetching oracles directly, use useLoopOraclePriceQuotes hook instead.
 */
export function getLoopOraclesFromMap(
    loopExpanded: LoopExpanded | undefined,
    oraclePrices: Map<string, OracleQuote | undefined> | undefined
): {
    principalOracle: OracleQuote | undefined;
    collateralOracle: OracleQuote | undefined;
} {
    const principalMint = getLoopExpandedPrincipalMint(loopExpanded);
    const collateralMint = getLoopExpandedCollateralMint(loopExpanded);

    return {
        principalOracle: principalMint ? oraclePrices?.get(principalMint) : undefined,
        collateralOracle: collateralMint ? oraclePrices?.get(collateralMint) : undefined
    };
}

export function useLoopOraclePriceQuotes(loopPositionExpanded: LoopExpanded | undefined): {
    principalOracle: OracleQuote | undefined;
    collateralOracle: OracleQuote | undefined;
} {
    const principalMint = getLoopExpandedPrincipalMint(loopPositionExpanded);
    const collateralMint = getLoopExpandedCollateralMint(loopPositionExpanded);
    const { getOracle } = useOraclePrices([principalMint, collateralMint]);

    const principalOracle = getOracle(principalMint);
    const collateralOracle = getOracle(collateralMint);

    return {
        principalOracle,
        collateralOracle
    };
}

export function getNetPosition(
    loopPosition: LoopPositionExpanded | undefined,
    principalUsdPrice: number | undefined,
    collateralUsdPrice: number | undefined
) {
    if (!loopPosition) return undefined;

    const loopCollateral = getZcCollateral(loopPosition?.loanExpanded);
    const ledger = getZcLedger(loopPosition?.loanExpanded);
    const totalDebt = ledger?.ledgerDebt.total;
    const loanExpanded = loopPosition.loanExpanded;

    // usdNpv is server-stamped — same pricing path as the section aggregate, so row-sum
    // reconciles. tokenNpv still uses client math because the native-token net position
    // isn't part of the server per-item shape.
    const usdNpv =
        (loanExpanded?.collateralUsd ?? 0) -
        (loanExpanded?.principalUsd ?? 0) -
        (loanExpanded?.interestAccruedUsd ?? 0);

    if (isWhirlpoolLoop(loopPosition.loopExpanded)) {
        const orcaCollateral = orcaCollateralPrincipalInfo(loopPosition);
        return {
            tokenNpv: bsMath.sub(orcaCollateral?.totalCollateralInPrincipalToken, totalDebt),
            usdNpv
        };
    }

    const totalDebtUsd = bsMath.mul(totalDebt, principalUsdPrice);
    const principalInCollateral = bsMath.div(totalDebtUsd, collateralUsdPrice);

    return {
        tokenNpv: bsMath.sub(loopCollateral?.amount, principalInCollateral),
        usdNpv
    };
}

export const MIN_TIME_FOR_RECENT_START = TIME.MINUTE * 30;

export function useLoopRecentStartTime(loopPosition: LoopPositionExpanded | undefined): {
    recentStartTime: boolean;
    isLoading: boolean;
} {
    const now = getUnixTs();
    const whirlpoolCollateral = getWhirlpoolLoopCollateral(loopPosition);

    const whirlPoolCollateralMint = whirlpoolCollateral?.position.positionMint;

    // we only check whirlpool collateral here because there is a delay betweent tracking the mint yield of the nft, other collateral mints should be written beforehand
    const { data: collateralYield, isLoading } = useLatestCollateralYieldHistoryByMint(
        whirlPoolCollateralMint ? [whirlPoolCollateralMint] : undefined
    );

    if (!loopPosition) {
        return { recentStartTime: false, isLoading: true };
    }

    // Calculate if loop started recently based on time
    const timeSinceStart = now - loopPosition.loanExpanded?.loan.startTime;
    const isRecentByTime = Math.max(timeSinceStart, 0) < MIN_TIME_FOR_RECENT_START;

    // For Orca loops, also check if there's no yield history data
    const isRecentByYieldHistory = whirlPoolCollateralMint ? !collateralYield?.[whirlPoolCollateralMint] : false;

    return {
        recentStartTime: isRecentByTime || !!isRecentByYieldHistory,
        isLoading
    };
}

function getLastCloseEvents(events: ParsedLoanEventExpanded[] | undefined) {
    if (!events) return undefined;
    const sortedEvents = [...events].sort((a, b) => b.eventTime - a.eventTime);

    const lastRepayPrincipalEvent = sortedEvents.find((e) =>
        isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.RepayPrincipal)
    );
    const lastRemoveCollateralEvent = sortedEvents.find((e) =>
        isEventActionMetadataType(e.actionMetadata, DerivedLoanEventActionType.RemoveCollateral)
    );

    return {
        lastRepayPrincipalEvent,
        lastRemoveCollateralEvent
    };
}

// Temporary function for getting the loop direction without using Loop Tags. Assumes if collateral mint is a stablecoin, the loop is a short on the principal token.
// Explicit direction overrides for known principal–collateral pairs
const STABLECOIN_MINTS = [USDC_MINT];
const XSOL_MINT = "4sWNB8zGWHkh6UnmwiEtzNxL4XrN7uK9tosbESbJFfVs";

const LOOP_DIRECTION_MAP: Record<
    string, // principalMint
    Record<string, "Long" | "Short"> // collateralMint -> direction
> = {
    [XSOL_MINT]: {
        [WRAPPED_SOL_MINT]: "Short" // XSOL is 2x SOL -> short SOL
    }
};

export function getLeverageLoopDirection(loopPosition: LoopExpanded | undefined): "Long" | "Short" | undefined {
    if (!loopPosition || !isLeverageLoop(loopPosition)) return undefined;

    const vault = loopPosition.loopVault;
    const collateralMint = vault?.collateralMint;
    const principalMint = vault?.principalMint;

    if (!collateralMint || !principalMint) return undefined;

    if (STABLECOIN_MINTS.includes(collateralMint)) return "Short";

    const mapped = LOOP_DIRECTION_MAP[principalMint]?.[collateralMint];
    if (mapped) return mapped;

    return "Long";
}
export function useLoopTokenPriceChanges(
    mint: string | undefined,
    historicalPrices: OracleOHLC[] | undefined,
    periods: number[] = [TIME.DAY * 7]
): { data: (LoopTokenPriceChange | undefined)[]; loading: boolean } {
    const now = useLiveClock(REWARDS_TICK);
    const sorted = useMemo(() => {
        if (!historicalPrices || !mint) return undefined;
        return [...historicalPrices].sort(
            (a, b) => roundTimestamp(a.startTimestamp) - roundTimestamp(b.startTimestamp)
        );
    }, [historicalPrices, mint]);

    const data: (LoopTokenPriceChange | undefined)[] = useMemo(() => {
        if (!mint || !sorted) return Array(periods.length).fill(undefined);

        return periods.map((period) => {
            const cutoff = now - period;

            const start = sorted.find((p) => roundTimestamp(p.startTimestamp) >= cutoff)?.avgPrice;
            const end = sorted[sorted.length - 1]?.avgPrice;

            if (start === undefined || end === undefined) return undefined;

            const change = (end - start) / start;
            return { start, end, change } as LoopTokenPriceChange;
        });
    }, [mint, sorted, periods, now]);

    return { data, loading: !historicalPrices };
}

export function useUnderlyingTokenInfo(tokenMints: string[]) {
    const { data: collateralYields, isLoading: collateralYieldsLoading } =
        useLoopCollateralYieldLatestByMintsQuery(tokenMints);

    const { getOracle, isLoading: oraclePricesLoading } = useOraclePrices(tokenMints);

    const isLoading = collateralYieldsLoading || oraclePricesLoading;

    // Convert to the format expected by getPositionYield and calculateNetApyLive
    const oraclePricesMap = useMemoizedMap(
        tokenMints,
        (mint) => mint,
        (mint) => getOracle(mint)
    );

    return {
        collateralYields,
        oraclePrices: oraclePricesMap,
        isLoading
    };
}

export function calculateLoopFairMarketValue({
    collateralAmount,
    debtAmount,
    collateralOraclePrice,
    principalOraclePrice
}: {
    collateralAmount: number | undefined;
    debtAmount: number | undefined;
    collateralOraclePrice: number | undefined;
    principalOraclePrice: number | undefined;
}): number | undefined {
    const oraclePrincipalUsd = bsMath.mul(principalOraclePrice, debtAmount);

    const oracleCollateralUsd = bsMath.mul(collateralOraclePrice, collateralAmount);

    const fairMarketValue = bsMath.sub(oracleCollateralUsd, oraclePrincipalUsd);

    return fairMarketValue;
}

export function isPrincipalTokenWind(principalTokenMint: string | undefined, inputMint: string | undefined) {
    return inputMint === principalTokenMint;
}

export function buildWindQuoteRequest({
    loopExpanded,
    principalAmount,
    slippagePercentDecimals,
    inputAmount,
    clmmPosition,
    inputToken,
    windMint,
    skip,
    userWallet,
    principalUsdPrice
}: ExternalWindQuoteParams) {
    // Minimal param validation: all API-required params must exist and be valid. We do not require loan address.

    const valid =
        !!loopExpanded &&
        !!userWallet &&
        typeof slippagePercentDecimals === "number" &&
        typeof principalAmount === "number" &&
        typeof inputAmount === "number" &&
        !skip;

    // Only construct request if valid, always call the query for cache consistency

    if (!valid) return undefined;

    let principalAmountToLoop = principalAmount;

    const principalTokenWind = isPrincipalTokenWind(loopExpanded.principalToken.mint, inputToken?.mint);
    if (principalTokenWind && isLoopPrincipalWindDisabled(loopExpanded.loopVault)) {
        return undefined;
    }
    if (principalTokenWind || loopExpanded.inputToken.mint === loopExpanded.principalToken.mint) {
        if (clmmPosition && principalUsdPrice !== undefined) {
            principalAmountToLoop = principalAmount + inputAmount / principalUsdPrice;
        } else {
            principalAmountToLoop = principalAmount + inputAmount;
        }
    }

    const request: SwapParams = {
        slippageBps: decimalsToBps(slippagePercentDecimals, "bps"),
        userWallet: userWallet,
        inputMint: windMint ?? loopExpanded.principalToken.mint,
        outputMint: loopExpanded.collateralToken.mint,
        inputAmount: uiAmountToLamports(principalAmountToLoop, loopExpanded.principalToken.decimals),
        // Loop actions always compose into flash_borrow and always broadcast via Jito for tip-based
        // prioritization + atomic bundling when multi-tx. splitAtaCreates hoists ATA creates into
        // setupIxs so the caller can forward them to flash_borrow's setup_ixs slot (as its own tx
        // when unifySetup: false, enabling bundle detection).
        optionalParams: {
            useJito: true,
            splitAtaCreates: true
        }
    };

    return request;
}

/**
 * Custom hook to get PnL display values based on loop type
 * For yield-bearing loops (regular loops / in-kind PnL), returns token-based PnL with USD calculated from token * current oracle price
 * For leverage loops (cost-basis PnL), returns the backend-provided USD PnL and calculates token from USD / current oracle price
 */
export function useLoopPnlDisplayValues(loopPosition: LoopPositionExpanded | undefined, currentOraclePrice?: number) {
    const pnlStats = loopPosition?.loanExpanded?.pnlStats;
    const isLeverage = isLeverageLoop(loopPosition?.loopExpanded);

    return useMemo(() => {
        if (isLeverage) {
            // For leverage loops (cost-basis PnL), use USD PnL from backend
            // Calculate token display using current oracle price instead of backend's collateralPnl
            const displayTokenPnl =
                pnlStats?.usdPnl !== undefined && currentOraclePrice
                    ? pnlStats.usdPnl / currentOraclePrice
                    : pnlStats?.collateralPnl;

            return {
                displayUsdPnl: pnlStats?.usdPnl,
                displayTokenPnl
            };
        }

        // For non-leverage loops (in-kind PnL), the USD value is the in-kind collateral PnL re-priced
        // at the current collateral price. The backend now stamps this as `tokenUsdPnl` (same pricing
        // path as `collateralUsd`); fall back to `usdPnl` when it's absent (CLMM/LP collateral or no
        // current price). Token display is still the raw collateral PnL.
        const displayTokenPnl = pnlStats?.collateralPnl;
        const displayUsdPnl = pnlStats?.tokenUsdPnl ?? pnlStats?.usdPnl;

        return {
            displayUsdPnl,
            displayTokenPnl
        };
    }, [isLeverage, pnlStats?.collateralPnl, pnlStats?.usdPnl, pnlStats?.tokenUsdPnl, currentOraclePrice]);
}
